import { memo } from 'react';
import { Search, Filter, ChevronDown, Banknote, BanknoteX, Package, PackageCheck, PackageX, Truck, LayoutGrid, Table2, LayoutList, Share2 } from 'lucide-react';

const ICON_CLASS = 'w-4 h-4 inline mr-1';
const BTN_INACTIVE = 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-50';

interface FilterState {
  busqueda: string;
  estadosPago: string[];
  estadosEntrega: string[];
  formasEntrega: string[];
}

interface PedidosFiltersProps {
  filters: FilterState;
  showFilters: boolean;
  hasActiveFilters: boolean;
  vistaTabla: boolean;
  agruparPorProducto: boolean;
  onBusquedaChange: (value: string) => void;
  onToggleFilters: () => void;
  onToggleEstadoPago: (estado: string) => void;
  onToggleEstadoEntrega: (estado: string) => void;
  onToggleFormaEntrega: (forma: string) => void;
  onResetFilters: () => void;
  onToggleVista: () => void;
  onToggleAgrupacion: () => void;
  onCopiarResumen: () => void;
}

export const PedidosFilters = memo<PedidosFiltersProps>(({
  filters,
  showFilters,
  hasActiveFilters,
  vistaTabla,
  agruparPorProducto,
  onBusquedaChange,
  onToggleFilters,
  onToggleEstadoPago,
  onToggleEstadoEntrega,
  onToggleFormaEntrega,
  onResetFilters,
  onToggleVista,
  onToggleAgrupacion,
  onCopiarResumen,
}) => {
  return (
    <div className="space-y-3">
      {/* Búsqueda y botón de filtros en la misma fila */}
      <div className="flex items-center gap-2">
        {/* Búsqueda */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <input
            type="text"
            placeholder="Buscar pedidos..."
            value={filters.busqueda}
            onChange={(e) => onBusquedaChange(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          />
        </div>
        
        {/* Botón de filtros con badge */}
        <button
          onClick={onToggleFilters}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
            showFilters
              ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
              : 'bg-slate-100 text-slate-700 border-2 border-slate-200 hover:bg-slate-200'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">Filtros</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
              {(filters.estadosPago.length + filters.estadosEntrega.length + filters.formasEntrega.length)}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        
        {hasActiveFilters && (
          <button
            onClick={onResetFilters}
            className="hidden sm:flex items-center px-3 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap flex-shrink-0"
          >
            Limpiar
          </button>
        )}

        {/* Toggle agrupar por producto */}
        <button
          type="button"
          onClick={onToggleAgrupacion}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
            agruparPorProducto
              ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
              : BTN_INACTIVE
          }`}
          title="Agrupar por producto"
        >
          <LayoutList className="w-4 h-4" />
          <span className="hidden sm:inline">Agrupar</span>
        </button>

        {/* Toggle vista tarjetas / tabla */}
        <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden flex-shrink-0">
          <button
            type="button"
            onClick={() => { if (vistaTabla) onToggleVista(); }}
            className={`p-2 transition-colors ${vistaTabla ? 'bg-white text-slate-500 hover:bg-slate-50' : 'bg-blue-100 text-blue-700'}`}
            title="Vista tarjetas"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => { if (!vistaTabla) onToggleVista(); }}
            className={`p-2 transition-colors ${vistaTabla ? 'bg-blue-100 text-blue-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
            title="Vista tabla"
          >
            <Table2 className="w-4 h-4" />
          </button>
        </div>

        {/* Compartir resumen para WhatsApp */}
        <button
          type="button"
          onClick={onCopiarResumen}
          className="p-2 rounded-lg bg-white text-slate-600 border border-slate-300 hover:bg-slate-50 transition-colors flex-shrink-0"
          title="Copiar resumen para WhatsApp"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>
      
      {/* Panel de filtros colapsable */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
        aria-hidden={!showFilters || undefined}
        {...(showFilters ? {} : { inert: '' })}
      >
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex flex-wrap gap-6 items-start">
            {/* Estado de Pago */}
            <div className="min-w-[240px]">
              <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <Banknote className="w-3.5 h-3.5" />
                Estado de Pago
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onToggleEstadoPago('pagado')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.estadosPago.includes('pagado')
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : BTN_INACTIVE
                  }`}
                >
                  <Banknote className={ICON_CLASS} />
                  Pagado
                </button>
                <button
                  onClick={() => onToggleEstadoPago('no_pagado')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.estadosPago.includes('no_pagado')
                      ? 'bg-red-500 text-white shadow-sm'
                      : BTN_INACTIVE
                  }`}
                >
                  <BanknoteX className={ICON_CLASS} />
                  No Pagado
                </button>
              </div>
            </div>
            
            {/* Estado de Entrega */}
            <div className="min-w-[240px]">
              <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <Package className="w-3.5 h-3.5" />
                Estado de Entrega
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onToggleEstadoEntrega('entregado')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.estadosEntrega.includes('entregado')
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : BTN_INACTIVE
                  }`}
                >
                  <PackageCheck className={ICON_CLASS} />
                  Entregado
                </button>
                <button
                  onClick={() => onToggleEstadoEntrega('no_entregado')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.estadosEntrega.includes('no_entregado')
                      ? 'bg-red-500 text-white shadow-sm'
                      : BTN_INACTIVE
                  }`}
                >
                  <PackageX className={ICON_CLASS} />
                  No Entregado
                </button>
              </div>
            </div>

            {/* Forma de Entrega */}
            <div className="min-w-[240px]">
              <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <Truck className="w-3.5 h-3.5" />
                Forma de Entrega
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onToggleFormaEntrega('retiro')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.formasEntrega.includes('retiro')
                      ? 'bg-blue-500 text-white shadow-sm'
                      : BTN_INACTIVE
                  }`}
                >
                  <Package className={ICON_CLASS} />
                  Retiro
                </button>
                <button
                  onClick={() => onToggleFormaEntrega('despacho')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.formasEntrega.includes('despacho')
                      ? 'bg-orange-500 text-white shadow-sm'
                      : BTN_INACTIVE
                  }`}
                >
                  <Truck className={ICON_CLASS} />
                  Despacho
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
